import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import useRootSelector from 'common/hooks/useRootSelector';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { useRenderIntlMessage } from 'common/hooks/useRenderIntlMessage';
import useTimezoneMoment from 'common/hooks/useTimezoneMoment';
import { IMembershipInfoImt, MembershipStatus } from 'common/interfaces/membership';
import { ActionItem, ActionsMenu } from 'common/components';
import {
  CancelMembershipModal,
  FreezeMembershipModal,
  ReactivateMembershipModal,
} from 'common/components/PersonProfile/modals';
import {
  FrequencyTypes,
  PackageCostType,
  PackageCostTypes,
  PackageType,
  PackageTypes,
} from 'modules/services/constants/packages';
import { DEFAULT_DATE_FORMAT, DEFAULT_DATE_TIME_FORMAT } from 'common/constants/dateFormats';
import commonMessages from 'common/messages/messages';
import messages from 'common/components/PersonProfile/messages';
import frontDeskMessages from 'modules/front-desk/messages/messages';
import { PeakModuleForNewPersonType } from 'common/interfaces/steps';
import { QuestionnaireModal } from 'common/modals';
import ViewPackageInfoModal from '../ViewMembershipModal/ViewMembershipModal';
import { ActionResult } from 'common/constants';
import {
  selectAvailableMembershipPackageActions,
  selectAvailableMembershipPackageActionsLoading,
  selectCancelMembershipActionResult,
} from 'common/components/PersonProfile/state/membership/selectors';
import { IMembershipActionDTOImt } from 'common/components/PersonProfile/interfaces';
import { CheckInBlock } from 'common/components/PersonProfile/components/index';
import {
  fetchAvailableMembershipPackageActions,
  resetAvailableMembershipPackageActionsAction,
} from 'common/components/PersonProfile/state/membership/actions';
import { useAppDispatch } from 'store/hooks';
import usePermissionContext from 'common/hooks/context/usePermissionContext';
import { RouteParts } from 'constants/route.constants';

const useStyles = makeStyles((theme: Theme) => ({
  membershipCard: {
    '&:hover': {
      backgroundColor: theme.palette.background.default,
      cursor: 'pointer',
    },
  },
  membershipTitle: {
    marginRight: theme.spacing(1),
  },
  autoRenewText: {
    marginLeft: theme.spacing(0.5),
  },
  wrapText: {
    wordWrap: 'break-word',
  },
  packageTypeChip: {
    height: '20px',
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.text.secondary,
    borderRadius: 10,

    fontSize: '0.75rem',
    fontWeight: 700,
  },
  actionsLoader: {
    justifyContent: 'center',

    '&:hover': {
      background: 'none',
    },
  },
}));

interface IMembershipCard {
  personId: number;
  module?: PeakModuleForNewPersonType;
  membership: IMembershipInfoImt;
  openChangePackagePlan?: (id: number) => void;
  isOpenPOSPanel?: boolean;
  handlePOSPanelChange?: (isOpen: boolean) => void;
  handleNewMemberPanelChange?: (isOpen: boolean) => void;
  handleChangePackagePlanChange?: (isOpen: boolean) => void;
  disablePosBtn?: boolean;
}

const MembershipCard: FC<IMembershipCard> = ({
  personId,
  membership,
  module,
  openChangePackagePlan,
  handleChangePackagePlanChange,
  handleNewMemberPanelChange,
  handlePOSPanelChange,
  isOpenPOSPanel,
  disablePosBtn,
}) => {
  const dispatch = useAppDispatch();
  const { MEMBER_SERVICE_FREEZE } = usePermissionContext();

  const cancelMembershipActionResult: ActionResult = useRootSelector(
    selectCancelMembershipActionResult,
  );
  const availableMembershipPackageActions: IMembershipActionDTOImt = useRootSelector(
    selectAvailableMembershipPackageActions,
  );
  const isMembershipPackageActionsLoading: boolean = useRootSelector(
    selectAvailableMembershipPackageActionsLoading,
  );

  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
  const [isOpenViewMembershipModal, setIsOpenViewMembershipModal] = useState(false);
  const [isFreezeModalOpen, setIsFreezeModalOpen] = useState<boolean>(false);
  const [isReactivateModalOpen, setIsReactivateModalOpen] = useState<boolean>(false);
  const [isChangePlanConfirmationModalOpen, setIsChangePlanConfirmationModalOpen] = useState<
    boolean
  >(false);
  const { CRM_LEAD_EDIT_MEMBERSHIP, PT_CRM_LEAD_EDIT_MEMBERSHIP } = usePermissionContext();

  const shouldOpenChangePackagePlanModal = useRef(false);
  const [timezoneMoment] = useTimezoneMoment();
  const renderIntlMessage = useRenderIntlMessage();
  const classes = useStyles();
  const pricePerBilling = membership?.get('pricePerBilling');
  const paymentSchedule = membership?.get('paymentSchedule');
  const startDate = membership?.get('startDate');
  const endDate = membership?.get('endDate');
  const membershipId = membership?.get('id');
  const membershipStatus = membership?.get('status');
  const cancellationDate = membership?.get('cancellationDate');

  const hasChangePermission = useMemo(() => {
    if (window.location.pathname.includes(RouteParts.PT_CRM)) {
      return PT_CRM_LEAD_EDIT_MEMBERSHIP;
    } else if (window.location.pathname.includes(RouteParts.CRM)) {
      return CRM_LEAD_EDIT_MEMBERSHIP;
    }

    return false;
  }, [CRM_LEAD_EDIT_MEMBERSHIP, PT_CRM_LEAD_EDIT_MEMBERSHIP]);

  const isActiveMembership = membershipStatus === MembershipStatus.ACTIVE;

  useEffect(() => {
    if (
      cancelMembershipActionResult === ActionResult.SUCCESS_ACTION &&
      shouldOpenChangePackagePlanModal.current
    ) {
      if (openChangePackagePlan) {
        openChangePackagePlan(personId);
      }

      shouldOpenChangePackagePlanModal.current = false;
    }
  }, [cancelMembershipActionResult, openChangePackagePlan, personId]);

  const handleOpenChangePackagePlanModal = useCallback(() => {
    if (isActiveMembership) {
      setIsChangePlanConfirmationModalOpen(true);
    } else {
      if (openChangePackagePlan) {
        openChangePackagePlan(personId);
      }
    }
  }, [isActiveMembership, openChangePackagePlan, personId]);

  const onToggleCancelMembershipModal = useCallback(() => {
    setIsCancelModalOpen(!isCancelModalOpen);
  }, [isCancelModalOpen]);

  const onToggleFreezeMembershipModal = useCallback(
    () => setIsFreezeModalOpen(!isFreezeModalOpen),
    [isFreezeModalOpen],
  );

  const onToggleReactivateMembershipModal = useCallback(
    () => setIsReactivateModalOpen(!isReactivateModalOpen),
    [isReactivateModalOpen],
  );

  const fetchAvailableActions = useCallback(() => {
    if (module) {
      dispatch(fetchAvailableMembershipPackageActions(personId, module));
    }
  }, [personId, module, dispatch]);

  const resetAvailableActions = useCallback(() => {
    dispatch(resetAvailableMembershipPackageActionsAction());
  }, [dispatch]);

  const renderActions = () => (
    <>
      {!!membership?.size && (
        <ActionsMenu
          horizontal
          tableActionsMode
          iconColor="primary"
          onOpen={fetchAvailableActions}
          onClose={resetAvailableActions}
        >
          {isMembershipPackageActionsLoading && (
            <ActionItem onClick={undefined} className={classes.actionsLoader}>
              <CircularProgress color="primary" size="1rem" />
            </ActionItem>
          )}

          {!isMembershipPackageActionsLoading && (
            <>
              <ActionItem
                onClick={handleOpenChangePackagePlanModal}
                disabled={
                  !availableMembershipPackageActions?.get('canChangePlan') && hasChangePermission
                }
              >
                <FormattedMessage {...messages.changePlanModalTitle} />
              </ActionItem>

              <ActionItem
                onClick={onToggleCancelMembershipModal}
                disabled={
                  !availableMembershipPackageActions?.get('canCancel') && hasChangePermission
                }
              >
                <FormattedMessage {...messages.cancelMembershipModalTitle} />
              </ActionItem>

              {MEMBER_SERVICE_FREEZE ? (
                <ActionItem
                  onClick={onToggleFreezeMembershipModal}
                  disabled={
                    !availableMembershipPackageActions?.get('canFreeze') && hasChangePermission
                  }
                >
                  <FormattedMessage {...messages.freezeMembershipModalTitle} />
                </ActionItem>
              ) : null}

              <ActionItem
                onClick={onToggleReactivateMembershipModal}
                disabled={
                  !availableMembershipPackageActions?.get('canReactivate') && hasChangePermission
                }
              >
                <FormattedMessage {...messages.reactivateMembershipModalTitle} />
              </ActionItem>
            </>
          )}
        </ActionsMenu>
      )}
    </>
  );

  const onOpenViewPackage = () => {
    setIsOpenViewMembershipModal(true);
  };

  return (
    <CheckInBlock
      headerDivider
      title={<FormattedMessage {...commonMessages.membership} />}
      buttonGroup={renderActions()}
    >
      {membership?.size ? (
        <Box onClick={onOpenViewPackage} className={classes.membershipCard}>
          <Box mt={1.5}>
            <Typography gutterBottom>
              <Typography component="span" variant="h5" className={classes.membershipTitle}>
                {membership.get('title')}
              </Typography>

              <Typography
                component="span"
                className={classes.wrapText}
                variant="button"
                color="textSecondary"
              >
                {`(${renderIntlMessage(
                  isActiveMembership ? commonMessages.activeOption : commonMessages.inactiveOption,
                )})`}
              </Typography>
            </Typography>

            {membership.get('type') === PackageType.Trial && (
              <Box display="flex" alignItems="cemter" mt={0.5} mb={1} gridGap={8}>
                <Chip
                  size="small"
                  label={PackageTypes.translate(PackageType.Trial)}
                  className={classes.packageTypeChip}
                />

                {membership.get('costType') === PackageCostType.Free && (
                  <Chip
                    size="small"
                    label={PackageCostTypes.translate(PackageCostType.Free)}
                    className={classes.packageTypeChip}
                  />
                )}
              </Box>
            )}

            {!!pricePerBilling && (
              <Typography component="p" gutterBottom>
                <Typography component="span">{`$${pricePerBilling.toFixed(2)}`}</Typography>

                {paymentSchedule && (
                  <Typography component="span">
                    /{FrequencyTypes.translate(paymentSchedule)}
                  </Typography>
                )}
              </Typography>
            )}

            {!isActiveMembership && (
              <Typography component="p" gutterBottom>
                <FormattedMessage
                  {...commonMessages.reasonLabel}
                  values={{
                    reason: renderIntlMessage(
                      membership.get('canceled')
                        ? commonMessages.canceled
                        : frontDeskMessages.memberStatusExpiredMember,
                    ),
                  }}
                />
              </Typography>
            )}
          </Box>

          <Box>
            {!!(startDate && endDate) && (
              <Typography color="textSecondary" gutterBottom component="p">
                {`${timezoneMoment(startDate).format(DEFAULT_DATE_FORMAT)} - ${timezoneMoment(
                  endDate,
                ).format(DEFAULT_DATE_FORMAT)}`}
              </Typography>
            )}
          </Box>

          {cancellationDate && (
            <Typography variant="caption" color="error">
              <FormattedMessage
                {...messages.cancellationDate}
                values={{
                  date: timezoneMoment(cancellationDate).format(DEFAULT_DATE_TIME_FORMAT),
                }}
              />
            </Typography>
          )}
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gridGap={16} mt={2}>
          <Typography color="textSecondary">
            <FormattedMessage {...commonMessages.noActiveMembershipTitle} />
          </Typography>

          <Button
            variant="contained"
            fullWidth
            onClick={handleOpenChangePackagePlanModal}
            hidden={hasChangePermission}
          >
            <FormattedMessage {...commonMessages.addMembershipBtn} />
          </Button>
        </Box>
      )}

      {isCancelModalOpen && module && (
        <CancelMembershipModal
          handleChangePackagePlanChange={handleChangePackagePlanChange || (() => {})}
          disablePosBtn={!!disablePosBtn}
          handleNewMemberPanelChange={handleNewMemberPanelChange || (() => {})}
          handlePOSPanelChange={handlePOSPanelChange || (() => {})}
          isOpenPOSPanel={!!isOpenPOSPanel}
          isOpen
          personId={personId}
          membershipId={membershipId}
          onCancel={onToggleCancelMembershipModal}
          module={module}
          isMembershipCard
        />
      )}

      {isFreezeModalOpen && module && (
        <FreezeMembershipModal
          module={module}
          isOpen={isFreezeModalOpen}
          personId={personId}
          onSubmit={onToggleFreezeMembershipModal}
          onCancel={onToggleFreezeMembershipModal}
          isMembershipCard
        />
      )}

      {isReactivateModalOpen && module && (
        <ReactivateMembershipModal
          handleChangePackagePlanChange={handleChangePackagePlanChange || (() => {})}
          disablePosBtn={!!disablePosBtn}
          handleNewMemberPanelChange={handleNewMemberPanelChange || (() => {})}
          handlePOSPanelChange={handlePOSPanelChange || (() => {})}
          isOpenPOSPanel={!!isOpenPOSPanel}
          isOpen={isReactivateModalOpen}
          personId={personId}
          onSubmit={onToggleReactivateMembershipModal}
          onCancel={onToggleReactivateMembershipModal}
          module={module}
          isMembershipCard
        />
      )}

      {isOpenViewMembershipModal && module && (
        <ViewPackageInfoModal
          module={module}
          isOpen
          packageId={membership?.get('id')}
          personId={personId}
          onClose={() => setIsOpenViewMembershipModal(false)}
        />
      )}

      {isChangePlanConfirmationModalOpen && (
        <QuestionnaireModal
          isOpen={isChangePlanConfirmationModalOpen}
          onClose={() => setIsChangePlanConfirmationModalOpen(false)}
          onSubmit={() => {
            setIsCancelModalOpen(true);
            setIsChangePlanConfirmationModalOpen(false);
            shouldOpenChangePackagePlanModal.current = true;
          }}
          body={<FormattedMessage {...messages.changePlanConfirmationMessage} />}
          submitBtnTitle={<FormattedMessage {...commonMessages.yesOption} />}
          cancelBtnTitle={<FormattedMessage {...commonMessages.noOption} />}
        />
      )}
    </CheckInBlock>
  );
};

export default MembershipCard;
